import { z } from "zod";

import {
  contractAiAnalysisConfidenceSchema,
  contractAiAnalysisSourceRefSchema,
} from "./contractAiAnalysis";

/**
 * RİSKLİ / EKSİK HÜKÜM BULGUSU (CLOS-206 `[189]` · alt görev CLOS-1001).
 *
 * ### ⚠ KAYNAK VE GÜVEN SÖZLÜĞÜ ANALİZDEN GELİYOR, YENİDEN YAZILMIYOR
 *
 * `contractAiAnalysisSourceRefSchema` ve `...ConfidenceSchema` buraya İTHAL EDİLİYOR.
 * İkinci bir "kaynak referansı" tanımı yazsaydık aynı kavram iki şekilde doğrulanır,
 * ekran iki farklı bileşenle çizer ve ikisi gün gelip ayrışırdı — `aiShared.tsx`'teki
 * `AiSourceReference` bugün tek bileşen ve bu ailenin de kartlarını çizecek.
 *
 * ### ⚠ "AI RİSK DERECESİ KARAR DEĞİL" ŞEMAYA DA YAZILDI
 *
 * Kart: *"AI risk derecesi gerçek workflow önceliği, onay kararı, mutabakat, imza
 * uygunluğu veya hukuki yaptırım oluşturmaz."* Bu yüzden alan adı `severity` ama TÜRÜ üç
 * kademeli bir YARDIM ölçüsü; hiçbir yerde bir sayıya, sıraya ya da SLA'ya çevrilmiyor
 * ve `CONTRACT_AI_RISK_ADVISORY` cümlesi ekranın bu ayrımı sessizce yutmasını engelliyor.
 */

/**
 * BULGU KATEGORİLERİ — kartın saydığı dört alan, fazlası yok.
 *
 * ⚠ `missing` DİĞER ÜÇÜNDEN AYRI: ötekiler metinde DURAN bir şeye işaret ediyor, bu ise
 * metinde OLMAYAN bir şeye. Ayrımın somut karşılığı aşağıdaki kaynak kuralı: eksik hüküm
 * bulgusunun gösterecek bir alıntısı yoktur ve olmayan bir alıntıyı zorunlu kılmak modele
 * "uydur" demek olurdu.
 */
export const CONTRACT_AI_RISK_CATEGORIES = [
  "risky",
  "ambiguous",
  "contradictory",
  "missing",
] as const;
export type ContractAiRiskCategory = (typeof CONTRACT_AI_RISK_CATEGORIES)[number];

export const CONTRACT_AI_RISK_CATEGORY_NAMES: Record<ContractAiRiskCategory, string> = {
  risky: "Riskli hüküm",
  ambiguous: "Belirsiz hüküm",
  contradictory: "Çelişkili hükümler",
  missing: "Eksik hüküm",
};

/**
 * ÖNEM — YALNIZ İNCELEME SIRASI İÇİN.
 *
 * ⚠ Adı `severity` ama anlamı "önce hangisine bakayım": kart risk derecesinin workflow
 * önceliği ya da onay kararı ÜRETMEMESİNİ şart koşuyor. Bu yüzden ne sayısal bir skor ne
 * de sıralanabilir bir alan var — üç ad, hepsi bu.
 */
export const CONTRACT_AI_RISK_SEVERITIES = ["high", "medium", "low"] as const;
export type ContractAiRiskSeverity = (typeof CONTRACT_AI_RISK_SEVERITIES)[number];

export const CONTRACT_AI_RISK_SEVERITY_NAMES: Record<ContractAiRiskSeverity, string> = {
  high: "Öncelikle incelenmeli",
  medium: "İncelenmeli",
  low: "Bilgi amaçlı",
};

/**
 * ⚠ Bu cümle EKRANDA ZORUNLU (CLOS-1003 kabul kriteri) ve metni burada — ekran kendi
 * cümlesini yazsaydı, "kesin hukuki sonuç değildir" uyarısı bir gün bir yüzeyde
 * unutulurdu. `AI_OUTPUT_ADVISORY_LABEL`'ın (KARAR 11) risk ailesindeki tamamlayıcısı.
 */
export const CONTRACT_AI_RISK_ADVISORY =
  "Bu bulgular kesin hukuki sonuç, mevzuata uygunluk garantisi veya onay/imza kararı " +
  "değildir; incelemeyi kolaylaştırmak için sıralanmıştır.";

/**
 * TEK BULGU.
 *
 * ⚠ `sources` ÇOĞUL ve kuralı kategoriye bağlı (aşağıdaki `superRefine`):
 *   — `contradictory`: EN AZ İKİ kaynak. Kart: *"çelişen hükümler varsa iki veya daha
 *     fazla kaynak bölüm birlikte gösterilir; tek taraflı özetle çelişki uydurulmaz."*
 *     Tek kaynakla çelişki iddia eden bir bulgu, kullanıcının doğrulayamayacağı bir
 *     iddiadır — şema bunu kabul etmiyor.
 *   — `risky` / `ambiguous`: EN AZ BİR kaynak; metinde duran bir şeyden söz ediyor.
 *   — `missing`: kaynak YOK (boş dizi zorunlu). Eksik hükmün alıntısı olamaz; model
 *     yine de bir alıntı verirse bu, metinde duran bir şeyi "eksik" diye sunmasıdır.
 */
export const contractAiRiskFindingSchema = z
  .strictObject({
    category: z.enum(CONTRACT_AI_RISK_CATEGORIES),
    severity: z.enum(CONTRACT_AI_RISK_SEVERITIES),
    confidence: contractAiAnalysisConfidenceSchema,
    /** Kısa başlık — kullanıcı listede bunu okuyor. */
    title: z.string().min(1).max(200),
    /**
     * GEREKÇE — AI YORUMU (kaynak metinden ayrı tutuluyor, `sources` ile aynı gerekçe).
     *
     * ⚠ "Bu hüküm hukuka aykırı" gibi kesin bir hükümden kaçınmak MODELİN yönergesinde;
     * şema bunu ölçemez ama gerekçeyi ZORUNLU kılarak gerekçesiz bir etiketi (yalnız
     * "riskli") engeller.
     */
    rationale: z.string().min(1).max(2000),
    sources: z.array(contractAiAnalysisSourceRefSchema).max(5),
  })
  .superRefine((bulgu, ctx) => {
    const yol = { path: ["sources"] };
    if (bulgu.category === "contradictory" && bulgu.sources.length < 2) {
      ctx.addIssue({
        code: "custom",
        message: "Çelişki bulgusu en az iki kaynak bölüm göstermelidir",
        ...yol,
      });
    }
    if (
      (bulgu.category === "risky" || bulgu.category === "ambiguous") &&
      bulgu.sources.length < 1
    ) {
      ctx.addIssue({ code: "custom", message: "Bulgu bir kaynak bölüme bağlanmalıdır", ...yol });
    }
    if (bulgu.category === "missing" && bulgu.sources.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: "Eksik hüküm bulgusu kaynak alıntı taşıyamaz",
        ...yol,
      });
    }
  });
export type ContractAiRiskFinding = z.infer<typeof contractAiRiskFindingSchema>;

/**
 * ÇALIŞMANIN TAMAMI.
 *
 * ⚠ `reviewNeeded` BULGU DEĞİL, BULGU ÜRETİLEMEYEN ALANLARIN LİSTESİ. Kart: *"Kaynakta
 * yeterli kanıt yoksa sistem bulgu üretmek yerine inceleme gereğini veya belirsizliği
 * açıkça gösterir."* Bu alan olmasaydı modelin iki seçeneği kalırdı: susmak (kullanıcı
 * "risk yok" sanır) ya da uydurmak. Boş bir `findings` dizisi TEK BAŞINA "risk yok"
 * demek DEĞİL — ekran bu ikisini ayrı gösteriyor.
 */
export const contractAiRiskOutputSchema = z.strictObject({
  findings: z.array(contractAiRiskFindingSchema).max(100),
  reviewNeeded: z.array(z.string().min(1).max(300)).max(20).default([]),
});
export type ContractAiRiskOutput = z.infer<typeof contractAiRiskOutputSchema>;

/** `contract_ai_analyses` ile AYNI gerekçe: serbest metin yok, sağlayıcı gövdesi sızmasın. */
export const CONTRACT_AI_RISK_ERROR_CODES = ["provider_error", "invalid_output"] as const;
export type ContractAiRiskErrorCode = (typeof CONTRACT_AI_RISK_ERROR_CODES)[number];

export const CONTRACT_AI_RISK_ERROR_MESSAGES: Record<ContractAiRiskErrorCode, string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; tarama yapılamadı ve krediniz iade edildi.",
  invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
};

export const AI_RISK_NOT_SAVABLE_MESSAGE = "Yalnız tamamlanmış bir risk taraması kaydedilebilir.";

/**
 * ⚠ BULGU YOK ≠ RİSK YOK. Ekranın boş sonucu anlatan cümlesi burada; ekran kendi cümlesini
 * yazsaydı bir gün "risk bulunamadı" gibi KESİN bir ifadeye dönüşürdü — kartın açıkça
 * yasakladığı şey (*"Yetersiz kanıt 'risk yok' ... gibi kesin mesaja dönüşmez"*).
 */
export const CONTRACT_AI_RISK_EMPTY_MESSAGE =
  "Bu taramada işaretlenen bir hüküm çıkmadı. Bu, sözleşmede risk olmadığı anlamına gelmez.";

export const CONTRACT_AI_RISK_REVIEW_STATES = ["open", "reviewed", "dismissed"] as const;
export type ContractAiRiskReviewState = (typeof CONTRACT_AI_RISK_REVIEW_STATES)[number];

export const CONTRACT_AI_RISK_REVIEW_STATE_NAMES: Record<ContractAiRiskReviewState, string> = {
  open: "İncelenmedi",
  reviewed: "İncelendi",
  dismissed: "Yok sayıldı",
};

/**
 * BULGU DURUMU GÜNCELLEME (CLOS-1002).
 *
 * ⚠ `note` AYNI İSTEKTE: durumu değiştirmeden not düşmek de, not düşmeden durumu
 * değiştirmek de mümkün olmalı; iki ayrı uç açsaydık ekran iki istek gönderir ve biri
 * düştüğünde kullanıcı yarım bir sonuç görürdü.
 *
 * ⚠ `note` `null` GÖNDERİLEBİLİR: notu SİLMEK ile notu değiştirmemek farklı şeyler.
 * Alanı yalnız `string` yapsaydık kullanıcı yazdığı notu asla geri alamazdı.
 */
export const updateContractAiRiskFindingSchema = z.strictObject({
  reviewState: z.enum(CONTRACT_AI_RISK_REVIEW_STATES),
  note: z.string().max(2000).nullable().optional(),
});
export type UpdateContractAiRiskFindingInput = z.infer<typeof updateContractAiRiskFindingSchema>;

export type ContractAiRiskFindingView = {
  id: string;
  runId: string;
  contractId: string;
  contractVersionId: string;
  versionNumber: number;
  position: number;
  category: ContractAiRiskCategory;
  severity: ContractAiRiskSeverity;
  confidence: "high" | "medium" | "low";
  title: string;
  rationale: string;
  sources: { clause: string; quote: string }[];
  reviewState: ContractAiRiskReviewState;
  note: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  /** ⚠ Kaynak versiyon artık en güncel değilse bulgu BAYAT — ekran onu ayrı gösteriyor. */
  stale: boolean;
  /**
   * ⚠ TÜRETİLMİŞ (saklanmıyor): aynı kaynak sorununun iki koşudaki iki satırını
   * ilişkilendiren deterministik anahtar (CLOS-1084). Sunucu hesaplıyor ki ekran ve
   * testler aynı tanımı kullansın — istemcide hesaplansaydı iki tanım doğardı.
   */
  dedupKey: string;
  /** ⚠ Gerekçe kesin bir hukuki hüküm kuruyorsa ekran ayrıca uyarıyor (CLOS-1084). */
  certaintyFlagged: boolean;
  createdAt: string;
};

export type ContractAiRiskRunSummary = {
  id: string;
  contractId: string;
  contractVersionId: string;
  versionNumber: number;
  sourceKind: "document" | "extracted" | "ocr";
  ocrAverageConfidence: number | null;
  status: "completed" | "failed";
  output: ContractAiRiskOutput | null;
  errorCode: ContractAiRiskErrorCode | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  stale: boolean;
  saved: boolean;
  savedByName: string | null;
  savedAt: string | null;
  createdByName: string;
  createdAt: string;
};

/**
 * TARAMA İSTEĞİ — analizle AYNI gövde ve aynı gerekçe: metin İSTEMCİDEN GELMİYOR, sunucu
 * kaydın kendi versiyonundan üretiyor. `versionId` isteğe bağlı: verilmezse o andaki en
 * güncel değişmez versiyon seçilip KAYDEDİLİYOR (kartın kaynak bütünlüğü kriteri).
 */
export const createContractAiRiskScanSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir versiyon kimliği gerekli").nullish(),
});
export type CreateContractAiRiskScanInput = z.infer<typeof createContractAiRiskScanSchema>;

/**
 * KESİN HUKUKİ İFADE İŞARETİ (CLOS-1084 kalite matrisi).
 *
 * ### ⚠ MODELİN CÜMLESİ SİLİNMİYOR, ÜSTÜ ÇİZİLMİYOR — İŞARETLENİYOR
 *
 * Kart: *"eksik bilgiye dayalı olası problem ... 'hukuka aykırı', 'geçersiz', 'kesin risk'
 * gibi kaynakla desteklenmeyen KESİN HÜKME dönüşmemeli."* Üç seçenek vardı:
 *   1. Böyle bir çıktıyı REDDETMEK — kullanıcının kredisi modelin bir kelime seçimi
 *      yüzünden yanardı ve elinde hiçbir şey kalmazdı.
 *   2. Cümleyi SESSİZCE düzeltmek — AI'ın ne dediğini kullanıcıdan gizlemek olurdu; bu
 *      üründe çıktının kaynağı ve ham hâli kullanıcının hakkı.
 *   3. İŞARETLEMEK — ekran o bulgunun yanına "AI kesin bir ifade kullandı; bu bir hukuki
 *      görüş değildir" uyarısını koyar. Seçilen bu.
 *
 * ⚠ Liste DAR ve KÖK BİÇİMLERDEN: Türkçe çekim ekleri (-dır, -tır, -dir) sonradan
 * geldiği için önek eşleşmesi yeterli. Geniş bir liste ("risk", "ihlal") her bulguyu
 * işaretler ve uyarı hiçbir şey anlatmaz hâle gelirdi.
 */
export const RISK_CERTAINTY_PATTERNS = [
  "hukuka aykırı",
  "geçersiz",
  "yasaya aykırı",
  "mevzuata aykırı",
  "kesinlikle",
  "hükümsüz",
  "suç teşkil",
] as const;

export const CONTRACT_AI_RISK_CERTAINTY_WARNING =
  "AI bu bulguda kesin bir ifade kullandı; bu bir hukuki görüş ya da uygunluk kararı değildir.";

/** Gerekçe kesin bir hukuki hüküm kuruyor mu — büyük/küçük harften bağımsız. */
export function riskGerekcesiKesinlikIddiaEdiyor(gerekce: string): boolean {
  const kucuk = gerekce.toLocaleLowerCase("tr");
  return RISK_CERTAINTY_PATTERNS.some((kalip) => kucuk.includes(kalip));
}

/**
 * DETERMİNİSTİK DEDUP ANAHTARI (CLOS-1084: *"aynı kaynak problemi deterministik dedup
 * anahtarıyla ilişkilendirilebilmeli"*).
 *
 * ### ⚠ ANAHTAR BAŞLIKTAN DEĞİL KAYNAKTAN
 *
 * Model aynı sorunu iki koşuda iki farklı cümleyle anlatabiliyor ("Cezai şartta üst sınır
 * yok" / "Gecikme cezası tavansız"). Anahtarı başlıktan üretseydik aynı sorun iki ayrı
 * sorun görünürdü. Kaynak MADDE ise metnin kendisinden geliyor ve iki koşuda da aynı.
 *
 * ⚠ Eksik hüküm bulgusunun kaynağı yok (şema yasaklıyor); orada anahtar BAŞLIKTAN
 * üretiliyor — eksikliğin tek kimliği neyin eksik olduğudur.
 *
 * ⚠ Bu değer SAKLANMIYOR, türetiliyor: bir kolon eklemek onu bir kez yazıp sonsuza kadar
 * dondururdu ve anahtarın tanımı değiştiğinde eski satırlar sessizce eşleşmez olurdu.
 */
export function riskBulguAnahtari(bulgu: {
  category: ContractAiRiskCategory;
  title: string;
  sources: { clause: string }[];
}): string {
  const normalize = (m: string) =>
    m
      .toLocaleLowerCase("tr")
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .trim();

  const kaynaklar = bulgu.sources
    .map((k) => normalize(k.clause))
    .filter((k) => k.length > 0)
    .sort();

  /** Kaynaklar sıralanıyor: çelişkide iki maddenin sırası modele göre değişebilir. */
  const govde = kaynaklar.length > 0 ? kaynaklar.join("|") : normalize(bulgu.title);
  return `${bulgu.category}:${govde}`;
}
